import { BasicAuthServiceHelper } from '@/modules/Auth'
import APIRoutes from './APIRoutes'
import { flattenMapping, invertFlatMapping } from './dataTransformer'
import { Address, LatLng } from '@/dataV3/Common/Address'
import { DateTime } from 'luxon'

export interface DateRange {
  start: string | null
  end: string | null
}
// Define the FiltersValues interface
export interface FiltersValues {
  sortBy: string
  address: Address | LatLng | null
  period?: DateRange
  distance?: {
    min: number
    max: number
  }
  services?: string
  petCount?: number | null
  pets?: string[]
  petSize?: string[]
  features?: string[]
}

// Define a mapping between app keys and API keys
const transformationMap = {}

// Flatten the mapping
export const flatMap = flattenMapping(transformationMap)
export const reverseMap = invertFlatMapping(flatMap)

// Get services data (GET /v2/petsitting/account/services)
export const getSearchAsync = async (filtersValues: FiltersValues, page = 1, itemsPerPage = 20) => {
  const { path: urlPath, method } = APIRoutes.GetSearch
  const queryParams = new URLSearchParams()

  // Map filtersValues to query parameters
  if (filtersValues.sortBy) {
    queryParams.append('searchType', filtersValues.sortBy)
  } else {
    queryParams.append('searchType', 'chronologic')
  }

  if (filtersValues.address && filtersValues.address.latitude && filtersValues.address.longitude) {
    queryParams.append('latitude', filtersValues.address.latitude.toString())
    queryParams.append('longitude', filtersValues.address.longitude.toString())
  }

  queryParams.append('page', page.toString())
  queryParams.append('itemsPerPage', itemsPerPage.toString())

  // Map period
  if (filtersValues.period && filtersValues.period.start) {
    const startDateStr = DateTime.fromISO(filtersValues.period.start, { zone: 'local' }).toString()
    const endDateStr = filtersValues.period.end
      ? DateTime.fromISO(filtersValues.period.end, { zone: 'local' }).toString()
      : startDateStr
    queryParams.append('periodStart', startDateStr)
    queryParams.append('periodEnd', endDateStr)
  }

  // Map distance
  if (filtersValues.distance && filtersValues.distance.min != null && filtersValues.distance.max != null) {
    queryParams.append('distanceMin', filtersValues.distance.min.toString())
    queryParams.append('distanceMax', filtersValues.distance.max.toString())
  }

  // Map pet counts for all services
  if (filtersValues.petCount != null) {
    // Always send max animals for all services
    queryParams.append('walkMaxAnimals', filtersValues.petCount.toString())
    queryParams.append('homeVisitMaxAnimals', filtersValues.petCount.toString())
    queryParams.append('sittingAtCustomerHomeMaxAnimals', filtersValues.petCount.toString())
    queryParams.append('sittingAtPetsitterHomeMaxAnimals', filtersValues.petCount.toString())
  }

  // Handle single service selection
  if (filtersValues.services) {
    const service = Array.isArray(filtersValues.services) ? filtersValues.services[0] : filtersValues.services
    if (service) {
      switch (service) {
        case 'promenade':
          queryParams.append('supportWalk', 'true')
          break
        case 'visite':
          queryParams.append('supportHomeVisit', 'true')
          break
        case 'garde-chez-vous':
          queryParams.append('supportSittingAtCustomerHome', 'true')
          break
        case 'garde-chez-eux':
          queryParams.append('supportSittingAtPetsitterHome', 'true')
          break
        case 'walk':
          queryParams.append('supportWalk', 'true')
          break
        case 'homeVisit':
          queryParams.append('supportHomeVisit', 'true')
          break
        case 'sittingAtCustomerHome':
          queryParams.append('supportSittingAtCustomerHome', 'true')
          break
        case 'sittingAtPetsitterHome':
          queryParams.append('supportSittingAtPetsitterHome', 'true')
          break
        default:
          break
      }
    }
  }

  // Map pets
  if (filtersValues.pets && filtersValues.pets.length > 0) {
    filtersValues.pets.forEach((pet) => {
      switch (pet) {
        case 'dog':
          queryParams.append('supportDog', 'true')
          break
        case 'cat':
          queryParams.append('supportCat', 'true')
          break
        case 'nac':
          queryParams.append('supportNAC', 'true')
          break
        default:
          break
      }
    })
  }

  // Map pet sizes
  if (filtersValues.petSize && filtersValues.petSize.length > 0) {
    filtersValues.petSize.forEach((size) => {
      switch (size) {
        case 'little':
          queryParams.append('supportLittle', 'true')
          break
        case 'medium':
          queryParams.append('supportMedium', 'true')
          break
        case 'big':
          queryParams.append('supportBig', 'true')
          break
        case 'giant':
          queryParams.append('supportGiant', 'true')
          break
        default:
          break
      }
    })
  }

  // Map features with inverted values for negative features
  if (filtersValues.features && filtersValues.features.length > 0) {
    filtersValues.features.forEach((feature) => {
      // Features that need to be inverted (they have negative titles in the UI)
      const invertedFeatures = [
        'haveDog',
        'haveOwnANeuteredMaleDog',
        'haveOwnAnUnNeuteredMaleDog',
        'haveOwnASpayedFemaleDog',
        'haveOwnAnUnspayedFemaleDog',
        'haveCat',
        'haveChildren',
      ]

      if (invertedFeatures.includes(feature)) {
        // For negative features, send false when selected
        queryParams.append(feature, 'false')
      } else {
        // For positive features, send true when selected
        queryParams.append(feature, 'true')
      }
    })
  }

  const fullUrl = `${urlPath}?${queryParams.toString()}`
  const getProfileResponse = await BasicAuthServiceHelper.requestAuthenticatedAsync(fullUrl, method)

  // Transform the response if needed
  return getProfileResponse
}
